import client from './client';
import {
  HourlyStat,
  LogRange,
  LogSummary,
  RequestLogPage,
  SessionDetail,
  StatusFilter,
  TopUser,
  UserSessions,
} from '../types/logs';

const EMPTY_TOTALS: LogSummary['totals'] = {
  total_calls: 0,
  total_errors: 0,
  overall_success_rate: 0,
  avg_duration_ms: 0,
};

const RANGE_DAYS: Record<LogRange, number> = { '24h': 1, '7d': 7, '30d': 30 };

/**
 * logs.db writes `datetime('now')` — "YYYY-MM-DD HH:MM:SS" in UTC, with no
 * trailing Z. `new Date()` reads that shape as LOCAL time, which would slide
 * every timestamp on this page by the viewer's offset (5h30m here) and make
 * "last 24 hours" look like it ends tomorrow. Pin it to UTC explicitly.
 */
export function parseLogTimestamp(value: string | null | undefined): Date | null {
  if (!value) return null;
  const iso = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
    ? `${value.replace(' ', 'T')}Z`
    : value;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

/** "22 Aug, 4:32:08 pm" — logs need seconds, unlike the shared formatDateTime. */
export function formatLogTime(value: string | null | undefined): string {
  const d = parseLogTimestamp(value);
  if (!d) return '—';
  return `${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}, ${d
    .toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })
    .toLowerCase()}`;
}

/** The `from`/`to` pair for a header preset, in the format the gateway expects. */
export function rangeParams(range: LogRange): { from: string; to: string } {
  const now = new Date();
  const from = new Date(now.getTime() - RANGE_DAYS[range] * 86_400_000);
  const fmt = (d: Date) => d.toISOString().slice(0, 19).replace('T', ' ');
  return { from: fmt(from), to: fmt(now) };
}

export async function getLogSummary(range: LogRange): Promise<LogSummary> {
  const res = await client.get('/api/admin/logs/summary', { params: rangeParams(range) });
  const raw = res.data ?? {};
  return {
    from: raw.from ?? '',
    to: raw.to ?? '',
    endpoints: raw.endpoints ?? [],
    // Defaulted so an empty window renders zeros rather than blank cards.
    totals: { ...EMPTY_TOTALS, ...(raw.totals ?? {}) },
  };
}

export async function getRequests(params: {
  path?: string;
  method?: string;
  status?: StatusFilter;
  user_id?: string;
  range: LogRange;
  page?: number;
  limit?: number;
}): Promise<RequestLogPage> {
  const { range, ...rest } = params;
  const res = await client.get('/api/admin/logs/requests', {
    params: { ...rangeParams(range), ...rest },
  });
  return {
    logs: res.data?.logs ?? [],
    pagination: res.data?.pagination ?? { total: 0, page: 1, limit: 50, pages: 0 },
  };
}

export async function getErrors(params: {
  range: LogRange;
  page?: number;
  limit?: number;
}): Promise<RequestLogPage> {
  const { range, ...rest } = params;
  const res = await client.get('/api/admin/logs/errors', {
    params: { ...rangeParams(range), ...rest },
  });
  return {
    logs: res.data?.logs ?? [],
    pagination: res.data?.pagination ?? { total: 0, page: 1, limit: 50, pages: 0 },
  };
}

export async function getHourlyStats(): Promise<HourlyStat[]> {
  const res = await client.get('/api/admin/logs/stats');
  return res.data?.hourly ?? [];
}

export async function getTopUsers(): Promise<TopUser[]> {
  const res = await client.get('/api/admin/logs/users');
  return res.data?.users ?? [];
}

export async function getSession(sessionId: string): Promise<SessionDetail> {
  const res = await client.get(`/api/admin/logs/session/${encodeURIComponent(sessionId)}`);
  return {
    session: res.data?.session ?? null,
    requests: res.data?.requests ?? [],
  };
}

export async function getUserSessions(userId: string): Promise<UserSessions> {
  const res = await client.get(`/api/admin/logs/user/${encodeURIComponent(userId)}/sessions`);
  return {
    user_id: res.data?.user_id ?? userId,
    sessions: res.data?.sessions ?? [],
  };
}

/**
 * request_summary is a JSON string the gateway built from key NAMES only. Render
 * it as a readable hint; if it is ever not the shape we expect, show it verbatim
 * rather than throwing inside a table row.
 */
export function describeRequestSummary(summary: string | null): string | null {
  if (!summary) return null;
  try {
    const parsed = JSON.parse(summary);
    if (Array.isArray(parsed?.keys)) {
      return parsed.keys.length ? `fields: ${parsed.keys.join(', ')}` : 'empty body';
    }
    // The auth/payment shapes are flag objects — show only the true ones.
    const present = Object.entries(parsed)
      .filter(([, v]) => v === true)
      .map(([k]) => k.replace(/^has_/, ''));
    return present.length ? `sent: ${present.join(', ')}` : 'no recognised fields';
  } catch {
    return summary;
  }
}
